import { FirebaseAnalytics } from '@capacitor-firebase/analytics';

import { Logger } from '@mopla/logger';

/**
 * User id for the native Firebase Analytics SDK, coupled to analytics consent.
 *
 * On web GTM maps `user_id` onto the GA4 tag. Natively the user id is SDK
 * state, not an event — so the bridge hands `user_id_set` over to
 * setFirebaseUserId instead of logging it (see ./firebaseBridge).
 *
 * Consent Mode covers storage and ad signals, but not the user id — it is a
 * direct identifier that would otherwise be attached regardless of the
 * CookieScript decision. So it is only handed to the SDK while
 * `analytics_storage` is granted:
 * - the bridge reports the id via setFirebaseUserId
 * - ./firebaseConsent reports the analytics decision via setUserIdConsent,
 *   on every decision CookieScript reports
 * The id is set once both are there and cleared again when consent is
 * revoked. A consent granted later picks up the id that is already known.
 *
 * The native SDK persists the user id across app starts and WebView reloads,
 * while the state here starts fresh. So the first decision after a start is
 * always applied — otherwise a start with denied consent would look like "no
 * change" and an id set in an earlier session would keep being sent.
 */

/** `null` until the first decision after a start is known. */
let isAllowed: boolean | null = null;
let currentUserId: string | null = null;

const applyUserId = () => {
	const userId = isAllowed ? currentUserId : null;

	FirebaseAnalytics.setUserId({ userId }).catch((err: unknown) =>
		Logger.getInstance('UserTracking').error(
			'Firebase user id: setUserId failed',
			err
		)
	);
};

/**
 * Takes the `user_id` of a `user_id_set` push. A non-empty string is set once
 * analytics consent is granted, until then it is only remembered. `null`
 * (pushed on sign-out / account deletion) clears it. Anything else is ignored.
 */
export const setFirebaseUserId = (userId: unknown) => {
	if (userId !== null && (typeof userId !== 'string' || !userId)) return;

	currentUserId = userId;
	if (isAllowed || userId === null) applyUserId();
};

/**
 * Applies the analytics decision to the user id: sets the known id when
 * granted, clears it when revoked.
 */
export const setUserIdConsent = (granted: boolean) => {
	if (granted === isAllowed) return; // never true for the first decision
	isAllowed = granted;
	applyUserId();
};
